import React, { useEffect } from "react";
import { useState } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

import "./HubzuContactUsComModal.css";
import { validateForm } from "@/utils/formValidator";
import { submitCommercialContactUsForm } from "@/api/contactUs";
import { TextFormatter } from "@/components/sell/utils/TextFormatter";
import { formatPhoneNumber } from "@/utils/phoneNumberFormatter";
import { useSelector } from "react-redux";

const HubzuContactUsComModal = ({ cmsData, isModalOpen, handleModal }) => {
  const user = useSelector((state) => state.user.user);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    phone: "",
    comments: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'

  const handleValueChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      // Remove any non-digit characters before checking length
      const digitsOnly = value.replace(/[^\d]/g, "");
      if (digitsOnly.length <= 10) {
        const formattedNumber = formatPhoneNumber(digitsOnly);
        setFormData((prevData) => ({ ...prevData, [name]: formattedNumber }));
      } else return;
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }

    // Clear errors on input change
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;

    // For phone number, strip non-digit characters before validation
    if (name === "phone") {
      const digitsOnly = value.replace(/[^\d]/g, "");
      const validationErrors = validateForm({ [name]: digitsOnly }, [name]);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name] || "",
      }));
    } else {
      const validationErrors = validateForm({ [name]: value }, [name]);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name] || "",
      }));
    }
  };

  const handleClear = () => {
    // Close the modal and clear the form after success
    handleModal();
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    });
    setTimeout(() => {
      setMessage("");
      setFormErrors({});
    }, 4000);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const formDataForValidation = {
      ...formData,
      phone: formData?.phone?.replace(/[^\d]/g, ""),
    };

    const formErrors = validateForm(
      formDataForValidation,
      Object.keys(formData)
    );
    if (Object.keys(formErrors).length > 0) {
      setFormErrors(formErrors);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const digitsOnly = formData?.phone?.replace(/[^\d]/g, "");
      const data = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: digitsOnly,
        comments: formData.comments,
        companyName: formData.companyName,
      };
      let response = await submitCommercialContactUsForm(data);

      if (response?.status === "FAILURE") {
        setMessage(
          response?.message || "Unable to Save the lead. Please try Again."
        );
        setMessageType("error");
        return;
      }
      setMessage("Your data has been successfully submitted!");
      setMessageType("success");
      handleClear();
    } catch (error) {
      setMessage("There was an error submitting the form. Please try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
      // Hide the message after 4 seconds
      setTimeout(() => setMessage(""), 4000);
    }
  };

  const handleLinkClick = (e) => {
    console.log(e);
    if (e.includes("#Terms") || e.includes("#&") || e.includes("#Conditions")) {
      window.open("/about/terms", "_blank");
    } else if (e.includes("#Privacy") || e.includes("#Policy.")) {
      window.open("/Privacy-Policy/", "_blank");
    }
  };

  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        firstName: user.userFirstName || "",
        lastName: user.userLastName || "",
        email: user.userEmailId || "",
        phone: user.primaryPhone || "",
      }));
    }
  }, [user]);

  return (
    <>
      {/* Success/Error Message */}
      {message && (
        <div
          className={`message ${messageType} contact-us-com-modal-container`}
        >
          <div className="contact-us-com-modal-message">
            <span className="message-icon me-2 mt-1">
              {messageType === "success" ? (
                <FaCheckCircle size={20} />
              ) : (
                <FaTimesCircle size={20} color="red" />
              )}
            </span>
            {message}
          </div>
        </div>
      )}
      {/* Contact Us Modal */}
      <Modal show={isModalOpen} onHide={handleModal} centered size="lg">
        <Modal.Header closeButton className="contact-us-com-modal-header-cont">
          <Modal.Title className="contact-us-com-modal-header">
            {cmsData?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem 4rem" }}>
          <p className="contact-us-com-modal-para">{cmsData?.description}</p>

          <Form onSubmit={handleFormSubmit}>
            <h6 className="contact-us-com-modal-info-text">
              CONTACT INFORMATION
            </h6>
            <div className="d-flex mt-3 mb-4">
              <div className="me-2 w-50 ">
                <Form.Control
                  className="me-2 border-0 bg-light"
                  placeholder="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleValueChange}
                  onBlur={handleBlur}
                />
                {formErrors.firstName && (
                  <div className="text-danger small mt-2">
                    {formErrors.firstName}
                  </div>
                )}
              </div>

              <div className="me-2 w-50 ">
                <Form.Control
                  className="me-2 border-0 bg-light"
                  placeholder="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleValueChange}
                  onBlur={handleBlur}
                />
                {formErrors.lastName && (
                  <div className="text-danger small mt-2">
                    {formErrors.lastName}
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <Form.Control
                className="mb-2 border-0 bg-light"
                placeholder="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleValueChange}
                onBlur={handleBlur}
              />
              {formErrors.companyName && (
                <div className="text-danger small mb-4">
                  {formErrors.companyName}
                </div>
              )}
            </div>
            <div className="mb-4">
              <Form.Control
                className="mb-2 border-0 bg-light"
                placeholder="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleValueChange}
                onBlur={handleBlur}
              />
              {formErrors.email && (
                <div className="text-danger small mb-4">{formErrors.email}</div>
              )}
            </div>
            <div className="mb-4">
              <Form.Control
                className="mb-2 border-0 bg-light"
                placeholder="Phone"
                name="phone"
                type="numeric"
                value={formData.phone}
                onChange={handleValueChange}
                onBlur={handleBlur}
              />
              {formErrors.phone && (
                <div className="text-danger small mb-4">{formErrors.phone}</div>
              )}
            </div>

            <h6 className="contact-us-com-modal-info-text">
              PROPERTY INFORMATION
            </h6>
            <div className="mb-4">
              <Form.Control
                as="textarea"
                rows={6}
                placeholder="Tell us about your commercial property"
                name="comments"
                value={formData.comments}
                onChange={handleValueChange}
                onBlur={handleBlur}
                className="mb-2 mt-3 border-0 bg-light"
              />
              {formErrors.comments && (
                <div className="text-danger small mb-4">
                  {formErrors.comments}
                </div>
              )}
            </div>

            <button
              className="btn btn-primary w-100"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Submit"
              )}
              {loading && " Submitting..."}
            </button>
            <p className="mt-3 small contact-us-com-modal-tc">
              <TextFormatter
                text={cmsData?.termsAndConditions}
                onLinkClick={handleLinkClick}
                style={{
                  lineHeight: "20px",
                  fontSize: "14px",
                }}
              />
            </p>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default HubzuContactUsComModal;
